import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowSmallLeftIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import debounce from 'debounce';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import useGlobalStore from 'hooks/useGlobalStore';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { enableBodyScroll } from 'utils/globalUtils';

const DEBOUNCE_TIME = 500;

export function SearchInput() {
    const navigate = useNavigate();
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });
    const { autocompleteOpen, searchQuery, setAutocompleteOpen, setSearchOpen, setSearchQuery } = useGlobalStore();
    const { pathname } = window.location ?? {};
    const searchInputRef = useRef(null);
    const [inputValue, setInputValue] = useState(searchQuery);

    const clearSearchQuery = () => {
        setInputValue('');
        setSearchQuery('');
        setAutocompleteOpen(false);
        enableBodyScroll(true);
    };

    const handleSubmitSearch = (e) => {
        e.preventDefault();
        if (pathname.includes('search') || pathname.includes('brand')) {
            window.location.href = `/shop/search?query=${inputValue}`;
        } else {
            navigate(`/shop/search?query=${inputValue}`);
            setAutocompleteOpen(false);
            enableBodyScroll(true);
        }
    };

    const debouncedSetSearchQuery = useCallback(
        debounce((value) => {
            setSearchQuery(value);
        }, DEBOUNCE_TIME),
        []
    );

    const handleUpdateQuery = (e) => {
        const { value } = e.target;
        setInputValue(value);
        debouncedSetSearchQuery(value);
    };

    useEffect(() => {
        if (mobile && !window.isNativeApp) {
            searchInputRef?.current?.focus();
        }
    }, []);

    return (
        <div
            className={clsx('bg-white px-15 py-10', {
                'fixed top-0 w-full overflow-hidden': window.isNativeApp && autocompleteOpen && inputValue !== '',
            })}
        >
            <div
                className={clsx('relative z-1300! overflow-hidden rounded-full bg-off-white px-10', {
                    'w-[200px] p-10 py-10 xl:w-[300px]': !mobile,
                    'px-10 py-5': mobile,
                })}
            >
                <div className="flex items-center gap-5">
                    <div>
                        {mobile && !window.isNativeApp ? (
                            <button
                                onClick={() => {
                                    setSearchOpen(false);
                                    setAutocompleteOpen(false);
                                    enableBodyScroll(true);
                                }}
                                type="button"
                                aria-label="Back"
                            >
                                <ArrowSmallLeftIcon width={20} className="relative top-[3px] mr-10" />
                            </button>
                        ) : (
                            <button onClick={() => searchInputRef?.current?.focus()} type="button" aria-label="Search">
                                <MagnifyingGlassIcon width={20} className="relative top-[3px]" />
                            </button>
                        )}
                    </div>
                    <form onSubmit={handleSubmitSearch} className="w-full overflow-hidden">
                        <input
                            className={clsx(
                                'w-full bg-off-white placeholder-grey outline-hidden hover:placeholder-black',
                                {
                                    'py-5': window.isNativeApp,
                                }
                            )}
                            ref={searchInputRef}
                            name="search"
                            id="search"
                            placeholder="Search"
                            value={inputValue}
                            onChange={handleUpdateQuery}
                            onFocus={() => {
                                setAutocompleteOpen(true);
                            }}
                        />
                    </form>
                    {inputValue !== '' && (
                        <button
                            type="button"
                            className="cursor-pointer"
                            onClick={clearSearchQuery}
                            aria-label="Clear search"
                        >
                            <XMarkIcon width={20} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
